// @ts-strict-ignore
import * as React from 'react';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Form from 'antd/lib/form';
import useForm from 'antd/lib/form/hooks/useForm';
import Text from 'antd/lib/typography/Text';
import { DownOutlined, TeamOutlined, UserAddOutlined } from '@ant-design/icons';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';
import { User, UserGroup } from '../../types';
import { OrgComponentProps, UserAppData } from '../../typescriptHelpers';
import FormItem from 'antd/lib/form/FormItem';
import Input from 'antd/lib/input';
import Divider from 'antd/lib/divider';
import Row from 'antd/lib/row';
import { antGrey, runwayColors, titleStyle } from '../styles/globalStyles';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Popover from 'antd/lib/popover';
import { getRunwayUserActionsForCategory, RunwayUserActionCategory } from '../../helpers/aclHelpers';
import { displayTextForAppPlatform, getUserActionDisplayName } from '../../helpers/displayHelpers';
import Link from 'antd/lib/typography/Link';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { globalMessage } from '../../utils/globalMessage';
import { tryUpdateOrgSettings } from '@/actions/orgSettings';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import { isDefaultUserGroup, userIsAdmin } from '../../helpers';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { RootState } from '@/reducers/reducers';
import Tooltip from 'antd/lib/tooltip';

interface AddEditUserGroupModalProps extends OrgComponentProps {
    open: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    orgGroups: UserGroup[];
    orgUsers: User[];
    appDataByAppId: Record<string, UserAppData>;
    currentGroup?: UserGroup;
    isDirectorySyncEnabled: boolean;
    isUsingAdHocGroups: boolean;
    disableEditingApps: boolean;
    usePermissionsOnGroupLevel: boolean;
}

const AddEditUserGroupModal: React.FC<AddEditUserGroupModalProps> = (props: AddEditUserGroupModalProps) => {
    const dispatch = useDispatch();
    const [form] = useForm();
    const [showExistingGroupsPopover, setShowExistingGroupsPopover] = React.useState(false);
    const isAdmin = useSelector((state: RootState) => userIsAdmin(state.user));
    const isUserEditingGroup = !!props.currentGroup;
    const okText = isUserEditingGroup ? 'Apply' : 'Create group';
    const disableFieldsForDefaultUserGroups = props.currentGroup ? isDefaultUserGroup(props.currentGroup) : false;
    const defaultActions = props.currentGroup?.defaultActions || [];
    const currentActions = Form.useWatch('allowedActions', form) || [];
    const isDefaultActionsSet = isEqual(currentActions, defaultActions);
    const userActionsSelectOptions = [
        {
            key: RunwayUserActionCategory.UACReleaseSettings,
            label: 'Release settings',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACReleaseSettings),
        },
        {
            key: RunwayUserActionCategory.UACReleaseActions,
            label: 'Release actions',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACReleaseActions),
        },
        {
            key: RunwayUserActionCategory.UACRegressionTesting,
            label: 'Regression testing',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACRegressionTesting),
        },
        {
            key: RunwayUserActionCategory.UACApprovals,
            label: 'Approvals',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACApprovals),
        },
        {
            key: RunwayUserActionCategory.UACChecklistItems,
            label: 'Checklist items',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACChecklistItems),
        },
        {
            key: RunwayUserActionCategory.UACAppManagement,
            label: 'App management',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACAppManagement),
        },
        {
            key: RunwayUserActionCategory.UACUserManagement,
            label: 'User management',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACUserManagement),
        },
        {
            key: RunwayUserActionCategory.UACPublicAPIAndWebhooks,
            label: 'Public API and Webhooks',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACPublicAPIAndWebhooks),
        },
        {
            key: RunwayUserActionCategory.UACSSOSAMLManagement,
            label: 'SSO/SAML management',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACSSOSAMLManagement),
        },
        {
            key: RunwayUserActionCategory.UACUserSettings,
            label: 'User settings',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACUserSettings),
        },
        {
            key: RunwayUserActionCategory.UACBuildDistroSettings,
            label: 'Build Distro settings',
            options: getRunwayUserActionsForCategory(RunwayUserActionCategory.UACBuildDistroSettings),
        },
    ];

    const { Option, OptGroup } = Select;

    const displayUserActionsSelectOptions = () => {
        return userActionsSelectOptions.map((group) => (
            <OptGroup label={group.label} key={group.key}>
                {group.options.length == 0 ?
                    <Option value={undefined} disabled={true}>
                        <Text type="secondary" disabled={true}>
                            {'No options'}
                        </Text>
                    </Option>
                :   group.options.map((userAction, _) => (
                        <Select.Option key={userAction} value={userAction}>
                            {getUserActionDisplayName(userAction)}
                        </Select.Option>
                    ))
                }
            </OptGroup>
        ));
    };

    const toggleExistingGroupsPopover = () => {
        setShowExistingGroupsPopover(!showExistingGroupsPopover);
    };

    const existingGroupsPopoverContent = (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {props.orgGroups.map((group) => (
                <Button
                    type="link"
                    style={{ color: antGrey.grey10, display: 'flex', alignItems: 'left' }}
                    key={group.id}
                    onClick={() => {
                        form.setFieldsValue({
                            allowedActions: group.actions != null ? group.actions : [],
                            groupMembers: group?.members,
                            appIds: group?.appIds ?? [],
                        });
                        setShowExistingGroupsPopover(false);
                    }}
                >
                    {group.name}
                </Button>
            ))}
        </div>
    );

    const addUserGroupIsLoading = useIsLoading(OrgSettingsUpdatable.CreateCustomGroup);
    const addUserGroupSuccess = useUpdated(OrgSettingsUpdatable.CreateCustomGroup);
    const addUserGroupError = useError(OrgSettingsUpdatable.CreateCustomGroup);

    const editUserGroupIsLoading = useIsLoading(OrgSettingsUpdatable.UpdateCustomGroup);
    const editUserGroupSuccess = useUpdated(OrgSettingsUpdatable.UpdateCustomGroup);
    const editUserGroupError = useError(OrgSettingsUpdatable.UpdateCustomGroup);

    const didSubmitRef = React.useRef(false);

    React.useEffect(() => {
        if (didSubmitRef.current && addUserGroupSuccess) {
            globalMessage.success('Custom group created successfully!');
            props.onSuccess();
            resetModalValues();
        }
    }, [addUserGroupSuccess]);

    React.useEffect(() => {
        if (didSubmitRef.current && addUserGroupError) {
            globalMessage.error(addUserGroupError);
        }
    }, [addUserGroupError]);

    React.useEffect(() => {
        if (didSubmitRef.current && editUserGroupSuccess) {
            globalMessage.success('Group edited successfully!');
            props.onSuccess();
            resetModalValues();
        }
    }, [editUserGroupSuccess]);

    React.useEffect(() => {
        if (didSubmitRef.current && editUserGroupError) {
            globalMessage.error(editUserGroupError);
        }
    }, [editUserGroupError]);

    React.useEffect(() => {
        if (props.open) {
            form.resetFields();
        }
    }, [props.open, props.currentGroup, form]);

    const resetModalValues = () => {
        props.onCancel();
    };

    const handleOnSubmit = () => {
        form.validateFields()
            .then((values) => {
                didSubmitRef.current = true;
                const baseRequest: any = {
                    orgId: props.orgId,
                    name: values.groupName,
                    userActions: values.allowedActions,
                    members: values.groupMembers,
                    appIds: values.appIds ?? [],
                    grantAdminToGroupUsers: values.grantAdminToGroupUsers ?? false,
                    grantItAdminToGroupUsers: values.grantItAdminToGroupUsers ?? false,
                };
                if (isUserEditingGroup) {
                    dispatch(
                        tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateCustomGroup, {
                            ...baseRequest,
                            id: props.currentGroup?.id,
                        }),
                    );
                } else dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.CreateCustomGroup, baseRequest));
            })
            .catch((_) => {
                return;
            });
    };

    return (
        <Modal
            open={props.open}
            title={isUserEditingGroup ? `Edit ${props.currentGroup?.name} group` : 'Create custom group'}
            okText={okText}
            width={700}
            onOk={() => {
                handleOnSubmit();
            }}
            cancelText={'Close'}
            okButtonProps={{
                loading: addUserGroupIsLoading || editUserGroupIsLoading,
                icon: isUserEditingGroup ? null : <UserAddOutlined />,
            }}
            onCancel={() => {
                resetModalValues();
            }}
            destroyOnClose
        >
            {!isUserEditingGroup && (
                <Text type="secondary">
                    Create a custom group with optional defined permissions, and specify users who will be members. You
                    can copy settings from an existing group by using the action below and modify it to your
                    preferences, or start from scratch.
                </Text>
            )}
            <Form
                form={form}
                style={{ marginTop: isUserEditingGroup ? 0 : 20 }}
                layout="vertical"
                preserve={false}
                initialValues={{
                    allowedActions: props.currentGroup?.actions != null ? props.currentGroup?.actions : [],
                    groupMembers: props.currentGroup?.members,
                    appIds: props.currentGroup?.appIds ?? [],
                    groupName: props.currentGroup?.name,
                    grantAdminToGroupUsers: props.currentGroup?.grantAdminToGroupUsers ?? false,
                    grantItAdminToGroupUsers: props.currentGroup?.grantItAdminToGroupUsers ?? false,
                }}
            >
                <Form.Item name="groupName" label="Group name" required={true}>
                    <Input
                        placeholder={'Enter a name'}
                        defaultValue={props.currentGroup?.name}
                        disabled={disableFieldsForDefaultUserGroups}
                    />
                </Form.Item>
                <Divider />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                    <Text style={{ ...titleStyle, fontSize: 14, color: antGrey.grey6 }}>Settings</Text>
                    <Popover
                        overlayClassName="existing-groups-popover"
                        overlayStyle={{ paddingBottom: '0px' }}
                        trigger="click"
                        open={showExistingGroupsPopover}
                        onOpenChange={(open) => {
                            setShowExistingGroupsPopover(open);
                        }}
                        placement="bottomRight"
                        content={existingGroupsPopoverContent}
                        getPopupContainer={(trigger) => trigger.parentElement}
                        destroyTooltipOnHide={true}
                    >
                        <Button
                            onClick={() => toggleExistingGroupsPopover()}
                            type="link"
                            icon={<TeamOutlined style={{ color: runwayColors.primary }} />}
                        >
                            <Space>
                                <Text style={{ color: runwayColors.primary }}>Copy from an existing group</Text>
                                <DownOutlined style={{ color: runwayColors.primary }} />
                            </Space>
                        </Button>
                    </Popover>
                </div>
                <FormItem
                    label="Allowed actions"
                    help={
                        <>
                            <Text type="secondary" style={{ marginBottom: 8 }}>
                                If no actions are specified, group members will have default read-only permissions.
                            </Text>
                            <br />
                            <Text type="secondary">
                                Read more about actions that can be associated with Groups from our{' '}
                                <Link
                                    href="https://docs.runway.team/using-runway/organization-settings/team#user-actions"
                                    target="_blank"
                                >
                                    docs
                                </Link>
                                {/* TODO: RUN-9089 Update read more link on create / update custom groups modal */}
                                {'.'}
                            </Text>
                        </>
                    }
                >
                    <Row gutter={8} align="middle">
                        <div style={{ flex: 1 }}>
                            <Form.Item name="allowedActions" noStyle>
                                <Select
                                    style={{ width: '100%' }}
                                    mode="multiple"
                                    placeholder="Select"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    maxTagCount={'responsive'}
                                >
                                    {displayUserActionsSelectOptions()}
                                </Select>
                            </Form.Item>
                        </div>
                        {disableFieldsForDefaultUserGroups && (
                            <>
                                <Button
                                    type="link"
                                    onClick={() =>
                                        form.setFieldsValue({
                                            allowedActions: props.currentGroup?.defaultActions || [],
                                        })
                                    }
                                    disabled={isDefaultActionsSet}
                                    style={{ paddingRight: 6 }}
                                >
                                    Reset
                                </Button>
                                <TooltipInfoIcon
                                    tooltipContent={
                                        isDefaultActionsSet ?
                                            'Default user permissions already applied to the ' +
                                            props.currentGroup?.name +
                                            ' group'
                                        :   <>
                                                Default {props.currentGroup?.name} permissions include the following:
                                                <ul>
                                                    {defaultActions.map((action) => (
                                                        <li key={action}>{getUserActionDisplayName(action)}</li>
                                                    ))}
                                                </ul>
                                            </>
                                    }
                                />
                            </>
                        )}
                    </Row>
                </FormItem>
                <br />
                <FormItem
                    label="Group members"
                    name="groupMembers"
                    help={
                        props.isDirectorySyncEnabled ? 'Group membership is managed by Directory Sync.'
                        : props.isUsingAdHocGroups ?
                            'Group membership is determined by ad-hoc groups mapping'
                        :   "Group membership can also be assigned to specific users directly in your organization's settings."

                    }
                >
                    <Select
                        disabled={props.isDirectorySyncEnabled || props.isUsingAdHocGroups}
                        mode="multiple"
                        placeholder="Select"
                        style={{ marginBottom: 8 }}
                        maxTagCount={'responsive'}
                        defaultValue={props.currentGroup?.members}
                        value={form.getFieldValue('groupMembers')}
                    >
                        {props.orgUsers.map((user) => (
                            <Select.Option key={user.id} value={user.id}>
                                {user.id}
                            </Select.Option>
                        ))}
                    </Select>
                </FormItem>
                <br />
                <FormItem
                    label="App membership"
                    name="appIds"
                    help={
                        props.disableEditingApps ?
                            'App membership is being managed by Directory Sync.'
                        :   'Members of this group will be granted write access to the selected apps in addition to any apps assigned to them directly.'
                    }
                >
                    <Select
                        disabled={props.disableEditingApps}
                        mode="multiple"
                        allowClear
                        placeholder="Select apps"
                        style={{ marginBottom: 8 }}
                        maxTagCount={'responsive'}
                        filterOption={(input, option) =>
                            (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {Object.values(props.appDataByAppId).map((appData) => (
                            <Option value={appData.appId} key={appData.appId} label={appData.appName}>
                                {appData.appName}{' '}
                                <Text type="secondary">
                                    ({displayTextForAppPlatform(appData.platform, appData.appStoreType)})
                                </Text>
                            </Option>
                        ))}
                    </Select>
                </FormItem>

                {props.usePermissionsOnGroupLevel && (
                    <>
                        <br />
                        <Tooltip
                            title="You do not have permission to perform this action."
                            open={!isAdmin ? undefined : false}
                        >
                            <FormItem name="grantAdminToGroupUsers" valuePropName="checked">
                                <Checkbox disabled={!isAdmin}>Grant users in this group Admin permissions.</Checkbox>
                            </FormItem>
                        </Tooltip>
                        <Tooltip
                            title="You do not have permission to perform this action."
                            open={!isAdmin ? undefined : false}
                        >
                            <FormItem name="grantItAdminToGroupUsers" valuePropName="checked">
                                <Checkbox disabled={!isAdmin}>Grant users in this group IT Admin permissions.</Checkbox>
                            </FormItem>
                        </Tooltip>
                    </>
                )}
            </Form>
        </Modal>
    );
};

export default AddEditUserGroupModal;
